import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { authApi } from "../apis/authApi";

export interface PsaStatus {
  hasPsaEnabled: boolean;
  mspId: number;
  userRole: string;
}

export function usePsaStatus() {
  const { user, isAuthenticated } = useAuth0();
  const [psaStatus, setPsaStatus] = useState<PsaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasCheckedRef = useRef(false);

  const checkPsaStatus = useCallback(async (force = false) => {
    if (!isAuthenticated || !user?.email) {
      setPsaStatus(null);
      return;
    }

    // Only check once per login session unless forced
    if (hasCheckedRef.current && !force) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await authApi.getPsaStatus(user.email);
      setPsaStatus({
        hasPsaEnabled: response.hasPsaEnabled,
        mspId: response.mspId,
        userRole: response.userRole,
      });
      hasCheckedRef.current = true;
    } catch (err: any) {
      setError(err.message || "Failed to check PSA status");
      setPsaStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.email]);

  useEffect(() => {
    checkPsaStatus();
  }, [checkPsaStatus]);

  // Reset check flag when user changes (logout/login)
  useEffect(() => {
    if (!isAuthenticated) {
      hasCheckedRef.current = false;
      setPsaStatus(null);
    }
  }, [isAuthenticated]);

  const refetch = useCallback(() => {
    hasCheckedRef.current = false;
    return checkPsaStatus(true);
  }, [checkPsaStatus]);

  return {
    psaStatus,
    isLoading,
    error,
    refetch,
  };
}

